import { SerializedError } from '@reduxjs/toolkit';
import type { BaseQueryFn, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import * as Sentry from '@sentry/react';
import { Mutex } from 'async-mutex';
import type { AxiosError, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import z, { ZodError } from 'zod';

import { getErrorMessage, standardErrorSchema } from './rest-api-error';

import { AuthStoreSlice } from '../../slices/auth';
import {
  triggerErrorModal,
  triggerUnexpectedErrorModal,
} from '../errorModalService';

import { getCurrentLanguage } from '~/contexts/lang/languageContext';
import { stripNull, stripUndefined } from '~/utils/helpers';
import { RootState } from '~/utils/store';

const mutex = new Mutex();

const authenticatedResponseSchema = z.object({
  user_type: z.number(),
  access_expiration: z.coerce.string().datetime(),
  refresh_expiration: z.coerce.string().datetime(),
  ws_access: z.string(),
  ws_refresh: z.string(),
  googleAuthAccountFlag: z.boolean().optional(),
});

const API_PATH = import.meta.env.VITE_API_PATH ?? '';
export const HTTPS_PROTOCOL = import.meta.env.VITE_HTTPS_PROTOCOL;

export const AUTH_API_PATH = import.meta.env.VITE_API_AUTH_PATH ?? '';
const IGNORED_ENDPOINTS_ON_401 = [
  '/login/code',
  '/tenant/login/code',
  '/tenant/login',
  '/tenant/login/resend-code',
  '/tenant/login/google',
  '/login/reference-key',
  '/login',
  '/reset-password',
  '/send-reset-password-email',
  '/logout',
];

const extraOptionsSchema = z
  .object({ requireAuth: z.boolean().catch(true) })
  .catch({ requireAuth: true });

type BaseQueryCustomOptions = Partial<z.infer<typeof extraOptionsSchema>>;
type BaseQueryArgs = {
  url: string;
  method?: AxiosRequestConfig['method'];
  data?: AxiosRequestConfig['data'];
  params?: AxiosRequestConfig['params'];
  baseURLOverride?: string;
  excludeAPIHeaders?: boolean;
} & Pick<AxiosRequestConfig, 'headers' | 'withCredentials' | 'responseType'>;

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' },
  ): BaseQueryFn<
    BaseQueryArgs &
      Pick<AxiosRequestConfig, 'headers' | 'withCredentials' | 'responseType'>,
    unknown,
    FetchBaseQueryError,
    BaseQueryCustomOptions
  > =>
  async (
    {
      url,
      method,
      data,
      params,
      baseURLOverride,
      excludeAPIHeaders,
      headers,
      withCredentials: withCredentialsArg,
      responseType,
    },
    _api,
    _extraOptions,
  ) => {
    try {
      const state = _api.getState() as RootState;
      const extraOptions = extraOptionsSchema.parse(_extraOptions);
      const currentLanguage = getCurrentLanguage();

      const withCredentials =
        withCredentialsArg !== undefined
          ? withCredentialsArg
          : extraOptions.requireAuth;
      const result = await axios({
        url: baseURLOverride ? baseURLOverride + url : baseUrl + url,
        method,
        data,
        params,
        headers: {
          ...headers,
          ...(!excludeAPIHeaders && { Region: state.auth.user.region }),
          ...(!excludeAPIHeaders && {
            'X-Frontend-Auth': import.meta.env.VITE_X_FRONTEND_AUTH,
          }),
          ...(!excludeAPIHeaders && {
            'Accept-Confide-Language': currentLanguage.code,
          }),
        },
        withCredentials,
        responseType,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      const standardErrorParse = standardErrorSchema.safeParse(
        err.response?.data,
      );
      if (standardErrorParse.success) {
        const isUnexpectedError =
          standardErrorParse.data.errors.some(
            (errorObj) => errorObj.code === 'unexpected',
          ) || err.response?.status === StatusCodes.INTERNAL_SERVER_ERROR;
        if (isUnexpectedError) {
          triggerUnexpectedErrorModal(err);
        } else {
          triggerErrorModal(err);
        }

        return {
          error: {
            status: err.response?.status ?? StatusCodes.BAD_REQUEST,
            data: standardErrorParse.data,
          } as FetchBaseQueryError,
        };
      }
      return {
        error: {
          status: err.response?.status ?? StatusCodes.BAD_REQUEST,
          data: err.response?.data || err.message,
        } as FetchBaseQueryError,
      };
    }
  };

const axiosBaseQueryWithReauth: BaseQueryFn<
  BaseQueryArgs & Pick<AxiosRequestConfig, 'headers' | 'withCredentials'>,
  unknown,
  FetchBaseQueryError,
  BaseQueryCustomOptions
> = async (args, api, extraOptions) => {
  const baseQuery = axiosBaseQuery({
    baseUrl: `${HTTPS_PROTOCOL}${API_PATH}`,
  });
  let result = await baseQuery(args, api, extraOptions ?? {});

  /**
   * Since we we only want to re-authenticate on 401 errors on endpoints after authentication,
   * we need to check if the endpoint we are requesting
   * is in the list of ignored endpoints.
   */
  const segments = args.url.split('/');
  const isSegmentIgnored = segments.some((segment) =>
    segment.includes(`/${segment}`),
  );
  const isEndpointIgnored =
    isSegmentIgnored || IGNORED_ENDPOINTS_ON_401.includes(args.url);

  if (result.error && result.error.status === 401 && !isEndpointIgnored) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // try to get a new token
        const refreshResult = await baseQuery(
          {
            url: '/token/refresh',
            method: 'POST',
            data: { is_http_cookie_only: true },
          },
          api,
          {},
        );
        const refreshedCredentials = authenticatedResponseSchema
          .pick({
            access_expiration: true,
            refresh_expiration: true,
            user_type: true,
          })
          .safeParse(refreshResult.data);

        if (refreshResult.data && refreshedCredentials.success) {
          const data = refreshedCredentials.data;
          api.dispatch(
            AuthStoreSlice.setCredentials({
              accessTokenExpiration: data.access_expiration,
              refreshTokenExpiration: data.refresh_expiration,
              type: data.user_type,
            }),
          );
          // retry the initial query
          result = await baseQuery(args, api, extraOptions ?? {});
        } else {
          window.location.href = `/logout?prevUrl=${window.location.pathname}`;
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }
  // catch errors that are not 401
  if (result.error && result.error.status !== 401) {
    // will log when query mutation request fails
    Sentry.captureException(new Error(getErrorMessage(result.error)), {
      level: 'warning',
      contexts: {
        request: {
          url: args.url,
          method: args.method,
        },
      },
    });
  }
  return result;
};

const TAG_TYPES = [
  'Tenant',
  'Users',
  'Reports',
  'Schedules',
  'Partner',
  'Localization',
] as const;
export const restAPI = createApi({
  reducerPath: 'api',
  baseQuery: axiosBaseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: TAG_TYPES,
});

export const defaultMutationResponseSchema = z.object({ message: z.string() });
export type DefaultMutationResponse = z.infer<
  typeof defaultMutationResponseSchema
>;

export const isMutationSuccess = <T = unknown>(
  result:
    | {
        data: T;
      }
    | {
        error: FetchBaseQueryError | SerializedError;
      },
): result is { data: T } => {
  if ('data' in result) return true;
  return false;
};

export const paginationMetaSchema = z.object({
  count: z.coerce.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  hasNext: z.boolean().optional().default(false),
  hasPrevious: z.boolean().optional().default(false),
});

export const parsePaginatedResponse = <Schema extends z.ZodTypeAny>(
  schema: Schema,
  data: unknown,
) => {
  return paginationMetaSchema
    .transform((values) => ({
      ...values,
      hasNext: values.next !== null,
      hasPrevious: values.previous !== null,
    }))
    .and(z.object({ results: schema.array() }))
    .parse(data);
};

export const paginatedParamsSchema = z.object({ page: z.coerce.number() });
export type PaginatedResponse<Data> = z.infer<typeof paginationMetaSchema> & {
  results: Data[];
};

export const toParams = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rawParams: Record<string, any>,
  options?: {
    stripNull?: boolean;
  },
) => {
  const { stripNull: shouldStripNull = true } = options ?? {};
  const paramArg = rawParams;
  let parsed = stripUndefined(paramArg ?? {});
  if (shouldStripNull) {
    parsed = stripNull(parsed);
  }

  return new URLSearchParams(parsed);
};

export function parseOrReturnError<T>(fn: () => T) {
  try {
    return { data: fn() };
  } catch (caughtError) {
    let error: FetchBaseQueryError;
    if (caughtError instanceof ZodError) {
      error = {
        status: 'CUSTOM_ERROR',
        error: 'Failed to parse the response data',
      };
    }
    error = {
      status: 'CUSTOM_ERROR',
      error: 'Failed to parse data',
    };
    return { error };
  }
}

/**
 * @ignore
 */
export function useWrappedMutation<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Trigger extends (...args: any[]) => any,
  Result,
  TMutation extends Readonly<[Trigger, Result]>,
  TriggerParameters extends Parameters<TMutation[0]>,
  MutationResponse extends Exclude<
    Awaited<ReturnType<TMutation[0]>>,
    { error: unknown }
  >,
>(args: TMutation) {
  return {
    mutateAsync: async (
      parameters: TriggerParameters[0],
      callbacks?: {
        onSuccess?: (data: MutationResponse['data']) => void;
        onError?: (error: FetchBaseQueryError | SerializedError) => void;
      },
    ) => {
      const result = await args[0](parameters);
      if (isMutationSuccess(result)) {
        callbacks?.onSuccess?.(result.data as MutationResponse);
      } else {
        callbacks?.onError?.(
          result.error as FetchBaseQueryError | SerializedError,
        );
      }
      return result as Awaited<ReturnType<TMutation[0]>>;
    },
    state: args[1] as TMutation[1],
  };
}
